import type { ComponentType } from "react";
import type { AccountType } from "@/types/api";

export interface GridLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

export type BreakpointKey = "2xl" | "xl" | "lg" | "md" | "sm";

export const BREAKPOINT_KEYS: BreakpointKey[] = ["2xl", "xl", "lg", "md", "sm"];

export type BreakpointLayouts = Record<BreakpointKey, GridLayoutItem[]>;

export type SizeVariant = "xs" | "s" | "m" | "l";

export interface SizeDefinition {
  w: number;
  h: number;
}

/**
 * Standardized widget sizes on a 12-column grid (72px row height, 12px margin):
 * - XS: 3×2  (tiny KPI tile, ~158px tall)
 * - S:  3×4  (compact panel)
 * - M:  6×4  (half-row standard panel)
 * - L:  12×5 (full-row panel)
 *
 * Widgets should opt-in to sizes that make visual sense, using these
 * presets as the baseline. Non-matching sizes are allowed as escapes.
 */
export const STANDARD_SIZES = {
  xs: { w: 3, h: 2 },
  s: { w: 3, h: 4 },
  m: { w: 6, h: 4 },
  l: { w: 12, h: 5 },
} as const;

/** Visual group — drives the widget library's "Charts" vs "Stats & lists"
 *  sections so users can find graph-based widgets separately from KPI/table
 *  widgets without having to scan descriptions. */
export type WidgetKind = "chart" | "value";

export interface WidgetDefinition {
  type: string;
  title: string;
  description: string;
  category: "overview" | "performance" | "activity" | "instructor" | "admin";
  /** Visual kind (chart vs non-chart) — default "value". */
  kind?: WidgetKind;
  roles?: AccountType[];
  /** Fixed size presets. At least one variant must be defined. */
  sizes: {
    xs?: SizeDefinition;
    s?: SizeDefinition;
    m?: SizeDefinition;
    l?: SizeDefinition;
  };
  /** Size used as default when the widget is added (must exist in `sizes`). */
  defaultSize?: SizeVariant;
  Icon?: ComponentType<{ className?: string }>;
  Component: ComponentType<WidgetComponentProps>;
  hideFromLibrary?: boolean;
  required?: boolean;
}

export interface WidgetComponentProps {
  instanceId: string;
  /** Current size preset: s / m / l. Component MUST branch its rendering on this. */
  sizeVariant: SizeVariant;
  breakpoint: BreakpointKey;
  editing: boolean;
}

/** Persisted per-instance metadata. Lives in widget_config_json next to the layout. */
export interface WidgetInstanceConfig {
  sizeVariant: SizeVariant;
}

export type WidgetConfigMap = Record<string, WidgetInstanceConfig>;
